import {
  useRequest,
  Config as UseRequestConfig,
  DataFetcherRequest,
} from '../useRequest';
import type {
  InferConfigType,
  InferFinalResponseType,
  InferResponseType,
  MakeAllKeysRequired,
} from '../types';
import {
  useGetConsideredAbTestsFromGrpcTrailers,
  useRequestGrpcClientChannel,
  useSetAbTestsFromTrailers,
} from './GrpcClientContext';
import {CacheKeyConfig, createCacheKey} from './createCacheKey';
import {isUnimplementedError} from './code';
import type {Observable} from 'protoc-gen-ts';
import {useUpdateGrpcRequestCache} from './useUpdateGrpcRequestCache';
import {useAttachOnInitialDataSuccessCallback} from './useAttachOnInitialDataSuccessCallback';
import {useRememberRequestsWithPossibleAbTestsBeforeCookieConsent} from './RefetchRequestsWithPendingAbTestActivations';
import {convertAllNullPropertiesToUndefined} from './convertAllNullPropertiesToUndefined';

export type GrpcRequestServiceMethod<Method = string> = {
  (...args: any[]): Promise<any> | Observable<any>;
  method: Method;
};

export type GrpcQueryConfig<T, ParamsType = unknown> = UseRequestConfig<T> &
  GrpcAdditionalConfig<T, ParamsType>;
export interface GrpcAdditionalConfig<T, ParamsType> {
  /**
   * An internally used unique identifier to generate the cache key. This key is generated automatically by a babel transform and should never be passed by hand.
   *
   */
  uniqueIdentifier?: string;
  /**
   * Allows to transform the internal cache key. This can e.g. become helpful if two different configurations will always return the same result. It was implemented to have the same result on category pages to request categories by pathname or id but don't do the request twice.
   */
  transformCacheKey?: (config: CacheKeyConfig) => CacheKeyConfig;
  /**
   * Allows to return mocked data for this endpoint. This should only be used for testing and the potential mock data should always be code splitted to not increase the production build.
   */
  mockData?: (params?: Record<string, unknown>) => Promise<T>;

  /**
   * If `not-implemented`, only returns the data in case a NotImplemented response is returned from the backend
   * @default 'not-implemented'
   */
  resolveMockData?: 'always' | 'not-implemented' | 'never';

  /**
   * `onRequestInitialDataSuccess` callback is triggered when a request instance made on the server side successfully returns mounts on the client side.
   * It passes the data returned from the server side request as the first argument.
   * Additionally, it passes:
   * - `invalidateCache` : a function to invalidate the cache for the request rendered on the server side
   * - `duplicateInitialDataByParams` : a function which allows to set the current data to a new set of params. Example: setting the data rendered on the server for 'female' but on the client we want to use that data for 'male'.
   * Thus basically `duplicateInitialDataByParams` allows to avoid duplicate requests when a param change after hydration.
   * And `invalidateCache` allows to invalidate the cache for the old params.
   */
  onRequestInitialDataSuccess?: (
    data: T,
    options: {
      duplicateInitialDataByParams: (params: ParamsType) => void;
      invalidateCache: () => void;
    },
  ) => void;
  /**
   * `onServerSuccess` is similar to `onSuccess` but is only called when the request is made on the server side and the data is returned successfully.
   * NOTE: This callback gives you the whole data returned from the server side request. For Bulk Requests it will give you the whole response object and not the individual sections.
   */
  onServerSuccess?: (data: T) => Promise<void>;
  /**
   * `validateInMemoryCacheRequest` is a fallback fetcher function that can be passed to validate the in-memory cache.
   * When defined, it will be called instead of the actual fetcher function first to see if the cache can be served.
   * If the cache cannot be served return `undefined` from this function you pass.
   * If this function returns `undefined` then the original fetcher will be called as usual.
   * If cached can be served, this function should return the same type as the original fetcher function.
   */
  validateInMemoryCacheRequest?: () => Promise<T | undefined>;
}

export type GrpcQueryConfigWithSection<
  T,
  ParamsType = unknown,
> = GrpcQueryConfig<T, ParamsType> & {
  section: keyof T;
};

export type GrpcTrailersConfig = {
  trailers?: Record<string, string[]>;
};

export const GRPC_HEADER_PROPERTY_NAME = Symbol('GrpcHeaderPropertyName');

export interface GenericGrpcRequestCallbackOptions {
  [GRPC_HEADER_PROPERTY_NAME]?: Record<string, string>;
}

/**
 * A convenience wrapper around `useRequest` to do GRPC requests.
 * The GRPC `channel` will be read from context.
 * @param ServiceImpl the GRPC service class to call
 * @param method the specific GRPC service function to call
 * @param params the parameters for the specific GRPC function
 */
export function useGenericGrpcRequest<
  ServiceMethod extends GrpcRequestServiceMethod,
  MethodName extends ServiceMethod['method'],
  GenericConfig,
  ResponseType extends InferResponseType<ReturnType<ServiceMethod>>,
  ParamsType extends MakeAllKeysRequired<
    Omit<Parameters<ServiceMethod>[1], keyof Partial<GenericConfig>>
  >,
  DefaultConfigType extends GrpcQueryConfigWithSection<
    ResponseType,
    ParamsType
  >,
  GrpcConfigType extends InferConfigType<
    MethodName,
    ResponseType,
    DefaultConfigType
  >,
  TData extends object = InferFinalResponseType<GrpcConfigType, ResponseType>,
>(
  maybeAsyncService: ServiceMethod | (() => Promise<ServiceMethod>),
  params: ParamsType,
  genericConfig: GenericConfig,
  grpcQueryConfig?: GrpcConfigType,
) {
  const {
    uniqueIdentifier: uniqueIdentifierGenerated,
    transformCacheKey,
    mockData,
    validateInMemoryCacheRequest,
    onServerSuccess,
    resolveMockData = 'not-implemented',
    onRequestInitialDataSuccess,
    ...queryConfig
  } = grpcQueryConfig ?? {};

  const uniqueIdentifier =
    uniqueIdentifierGenerated ??
    (process.env.NODE_ENV === 'test' && 'method' in maybeAsyncService
      ? (maybeAsyncService as ServiceMethod).method
      : undefined);

  const cacheKeyConfig = {
    uniqueIdentifier,
    params: convertAllNullPropertiesToUndefined(params),
  } as CacheKeyConfig;

  const key = createCacheKey(
    transformCacheKey ? transformCacheKey(cacheKeyConfig) : cacheKeyConfig,
  );

  useAttachOnInitialDataSuccessCallback({
    initialCacheKey: key,
    uniqueIdentifier,
    queryConfig,
    transformCacheKey,
    onRequestInitialDataSuccess,
  });

  const addAbTestsFromTrailers = useSetAbTestsFromTrailers();
  const getConsideredAbTestsFromGrpcTrailers =
    useGetConsideredAbTestsFromGrpcTrailers();

  const originalFetch = useGenericGrpcRequestFn(
    maybeAsyncService,
    params,
    genericConfig,
  );

  const updateGrpcRequestCache = useUpdateGrpcRequestCache(
    // @ts-expect-error useUpdateGrpcRequestCache expects ServiceMethod for correctly inferring param types
    maybeAsyncService,
    convertAllNullPropertiesToUndefined(params),
    grpcQueryConfig,
  );

  let fetcher = originalFetch;

  if (validateInMemoryCacheRequest) {
    fetcher = async () => {
      const cachedData = await validateInMemoryCacheRequest();
      const result = cachedData ?? (await originalFetch(params));

      /**
       * call onServerSuccess only for the original fetcher call and not for the validateInMemoryCacheRequest call
       * NOTE: this is simply to avoid calling onServerSuccess unnecessarily when we resolve the cache from the validateInMemoryCacheRequest
       */
      if (!process.browser && onServerSuccess && !cachedData) {
        onServerSuccess(result);
      }

      return result;
    };
  }

  if (resolveMockData !== 'never' && mockData) {
    fetcher = () => {
      return new Promise<ResponseType>((resolve, reject) => {
        if (resolveMockData === 'always') {
          mockData(params).then(resolve);
        } else {
          originalFetch()
            .then(resolve)
            .catch(async error => {
              if (
                isUnimplementedError(error) &&
                resolveMockData === 'not-implemented'
              ) {
                return mockData(params).then(resolve);
              }
              return reject(error);
            });
        }
      });
    };
  }

  const request = useRequest(key, fetcher, queryConfig);

  useRememberRequestsWithPossibleAbTestsBeforeCookieConsent({
    request,
    cacheKey: key,
  });

  const consideredAbTestsFromGrpcTrailers =
    getConsideredAbTestsFromGrpcTrailers(
      (request as DataFetcherRequest<TData & GrpcTrailersConfig>).data
        ?.trailers,
    );

  /**
   * the onServerSuccess callback is only called when the request is made on the server side and the data is returned successfully.
   * NOTE: we don't call it by default here if the `validateInMemoryCacheRequest` is defined because the `validateInMemoryCacheRequest` function should be responsible for calling it to avoid calling onServerSuccess unnecessarily.
   */
  if (
    !process.browser &&
    !validateInMemoryCacheRequest &&
    onServerSuccess &&
    request.data
  ) {
    onServerSuccess(request.data);
  }

  if (request.data && grpcQueryConfig && 'section' in grpcQueryConfig) {
    request.data = request.data[(grpcQueryConfig as DefaultConfigType).section];
  }

  request.updateCache = updateGrpcRequestCache;

  if (consideredAbTestsFromGrpcTrailers.length) {
    addAbTestsFromTrailers(consideredAbTestsFromGrpcTrailers);
  }

  return request as DataFetcherRequest<TData>;
}

export const useGenericGrpcRequestFn = <
  ServiceMethod extends GrpcRequestServiceMethod,
  GenericConfig,
  ParamsType extends Omit<Parameters<ServiceMethod>[1], keyof GenericConfig> &
    GenericGrpcRequestCallbackOptions,
  ResponseType extends InferResponseType<ReturnType<ServiceMethod>>,
>(
  maybeAsyncServiceMethod: ServiceMethod | (() => Promise<ServiceMethod>),
  params: ParamsType,
  genericConfig: GenericConfig,
) => {
  const requestGrpcClientChannel = useRequestGrpcClientChannel();

  return async (mutationParams?: ParamsType): Promise<ResponseType> => {
    const isSyncServiceMethod =
      typeof maybeAsyncServiceMethod === 'function' &&
      'method' in maybeAsyncServiceMethod;

    const [channel, SyncServiceMethod] = await Promise.all([
      requestGrpcClientChannel({
        legacy: Boolean((genericConfig as any).shopId),
      }),
      (isSyncServiceMethod
        ? maybeAsyncServiceMethod
        : await (maybeAsyncServiceMethod as any)()) as ServiceMethod,
    ]);
    let transformedMetadata;
    let requestParams:
      | Omit<ParamsType, typeof GRPC_HEADER_PROPERTY_NAME>
      | undefined;
    if (mutationParams?.[GRPC_HEADER_PROPERTY_NAME]) {
      // grpc is loaded async because it prevents large increase in size of main bundles
      // webpack has problem where grpc is not properly bundled and will add 20-25kb of size
      // to avoid that we load it async
      const grpcWeb = await import('@improbable-eng/grpc-web');

      transformedMetadata = mutationParams?.[GRPC_HEADER_PROPERTY_NAME]
        ? new grpcWeb.grpc.Metadata(mutationParams?.[GRPC_HEADER_PROPERTY_NAME])
        : undefined;
      const {[GRPC_HEADER_PROPERTY_NAME]: _, ...splitParams} = mutationParams;
      requestParams = splitParams;
    }

    // its important to spread `params` after `genericConfig` since in case of Grips requests the `params` can override the `genericConfig`
    return SyncServiceMethod(
      channel,
      {
        ...genericConfig,
        ...params,
        ...(requestParams || mutationParams),
      },
      transformedMetadata ? {metadata: transformedMetadata} : {},
    );
  };
};
